/**
 * @module HypothesesView
 * @purpose Stage-6 hypothesis view — resolves latest run, fetches hypotheses, renders card list.
 * @depends useReasoningRun, useHypotheses, HypothesisCard, types/reasoning, react-i18next
 * @dependedOnBy CaseAnalysisTab
 *
 * @rules
 * - Performance: no virtualisation — hypothesis cardinality is low (one per declared role)
 * - State: hooks own all async state; component is pure render given hook output
 * - Errors: guard clauses for loading/error/empty; evaluate errors surface via useHypotheses.error
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useReasoningRun } from '../../../../../hooks/use-reasoning'
import { useHypotheses } from '../../../../../hooks/use-hypotheses'
import { HypothesisCard } from './HypothesisCard'
import { LibraryPanel } from './LibraryPanel'
import { CatalogueSidebar } from './CatalogueSidebar'
import { partitionHypotheses } from './coverage'
import type { Hypothesis, ReasoningRun, SignalCounts } from '../../../../../types/reasoning'

interface HypothesesViewProps {
  caseId: string
  onViewBriefing?: () => void
}

interface CardListProps {
  hypotheses: Hypothesis[]
  signalCounts: SignalCounts
  totalObservations: number
  onViewBriefing: (() => void) | undefined
}

interface ViewHeaderProps {
  runId: string | undefined
  evaluating: boolean
  onEvaluate: () => void
}

interface ViewErrorStateProps {
  error: string
  onRetry: () => void
}

interface NoHypothesesStateProps {
  evaluating: boolean
  onEvaluate: () => void
}

const CONTENT_PADDING = '16px'
const SECTION_GAP = '16px'

const SHELL_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: SECTION_GAP,
  alignItems: 'flex-start',
}

const MAIN_COLUMN_STYLE: React.CSSProperties = {
  flex: '3 1 480px',
  minWidth: '0',
}

const LOADING_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.82rem',
  color: 'var(--text-muted)',
  padding: CONTENT_PADDING,
}

const HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--text)',
}

const BTN_ACTIVE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  padding: '4px 10px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
  cursor: 'pointer',
}

const BTN_DISABLED: React.CSSProperties = {
  ...BTN_ACTIVE,
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.85rem',
  color: 'var(--text-dim)',
  padding: '24px 0',
}

const EMPTY_SUBTITLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  marginTop: '4px',
  marginBottom: '12px',
}

function ViewLoadingState(): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div role="status" aria-busy={true} style={LOADING_STYLE}>
      {t('reasoning.hypothesis.view.loading')}
    </div>
  )
}

function ViewErrorState({ error, onRetry }: ViewErrorStateProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div role="alert" style={{ padding: CONTENT_PADDING }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
        {t('reasoning.hypothesis.view.errorTitle')}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '8px' }}>
        {error}
      </div>
      <button type="button" onClick={onRetry} style={BTN_ACTIVE}>
        {t('reasoning.hypothesis.view.errorRetry')}
      </button>
    </div>
  )
}

function ViewHeader({ runId, evaluating, onEvaluate }: ViewHeaderProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const btnLabel = evaluating
    ? t('reasoning.hypothesis.view.evaluatingButton')
    : t('reasoning.hypothesis.view.evaluateButton')
  const btnDisabled = runId === undefined || evaluating
  return (
    <div style={HEADER_STYLE}>
      <span style={TITLE_STYLE}>{t('reasoning.hypothesis.view.title')}</span>
      <button
        type="button"
        disabled={btnDisabled}
        onClick={onEvaluate}
        style={btnDisabled ? BTN_DISABLED : BTN_ACTIVE}
        aria-disabled={btnDisabled}
      >
        {btnLabel}
      </button>
    </div>
  )
}

function NoRunState(): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.view.noRunTitle')}</div>
      <div style={EMPTY_SUBTITLE}>{t('reasoning.hypothesis.view.noRunSubtitle')}</div>
      <button
        type="button"
        disabled
        aria-disabled={true}
        title={t('reasoning.hypothesis.view.noRunSubtitle')}
        style={BTN_DISABLED}
      >
        {t('reasoning.hypothesis.view.evaluateCta')}
      </button>
    </div>
  )
}

function NoHypothesesState({ evaluating, onEvaluate }: NoHypothesesStateProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.view.noHypothesesTitle')}</div>
      <div style={EMPTY_SUBTITLE}>{t('reasoning.hypothesis.view.noHypothesesSubtitle')}</div>
      <button
        type="button"
        disabled={evaluating}
        onClick={onEvaluate}
        style={evaluating ? BTN_DISABLED : BTN_ACTIVE}
        aria-disabled={evaluating}
      >
        {t('reasoning.hypothesis.view.evaluateCta')}
      </button>
    </div>
  )
}

interface ContentSwitchProps {
  run: ReasoningRun | null
  hypotheses: Hypothesis[]
  signalCounts: SignalCounts
  totalObservations: number
  evaluating: boolean
  onEvaluate: () => void
  onViewBriefing: (() => void) | undefined
}

function CardList({ hypotheses, signalCounts, totalObservations, onViewBriefing }: CardListProps): React.ReactElement {
  // Only hypotheses with supporting indicators earn a full workflow card; the rest drop to
  // the compact library side-list. This keeps unmatched patterns (e.g. a zero-match psexec
  // row) from occupying prominent space while still surfacing them for completeness.
  const { promoted, library } = partitionHypotheses(hypotheses)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SECTION_GAP }}>
      {promoted.map((hypothesis) => (
        <HypothesisCard
          key={hypothesis.hypothesisId}
          hypothesis={hypothesis}
          signalCounts={signalCounts}
          totalObservations={totalObservations}
          {...(onViewBriefing !== undefined ? { onViewBriefing } : {})}
        />
      ))}
      <LibraryPanel hypotheses={library} />
    </div>
  )
}

function ContentSwitch({ run, hypotheses, signalCounts, totalObservations, evaluating, onEvaluate, onViewBriefing }: ContentSwitchProps): React.ReactElement {
  if (run?.status !== 'completed') {
    return <NoRunState />
  }
  if (hypotheses.length === 0) {
    return <NoHypothesesState evaluating={evaluating} onEvaluate={onEvaluate} />
  }
  return (
    <CardList
      hypotheses={hypotheses}
      signalCounts={signalCounts}
      totalObservations={totalObservations}
      onViewBriefing={onViewBriefing}
    />
  )
}

/**
 * Stage-6 hypothesis view — container for the hypotheses sub-tab.
 *
 * @behaviour Resolves latest reasoning run, calls useHypotheses, and renders the card list.
 *   Guard clauses handle loading, error, no-completed-run, and empty-hypothesis states.
 *   Evaluate button triggers POST stage6-evaluate via the hook; disabled without a run ID.
 * @errors Loading/error/empty states rendered inline; evaluate errors surface via useHypotheses.error.
 */
export function HypothesesView({ caseId, onViewBriefing }: HypothesesViewProps): React.ReactElement {
  const { run, loading: runLoading, error: runError, refetch: refetchRun } = useReasoningRun(caseId, true)
  const runId = run?.reasoningRunId
  const {
    hypotheses, signalCounts, totalObservations,
    loading, error, evaluating, evaluate, refetch,
  } = useHypotheses(caseId, runId)

  const handleEvaluate = useCallback(() => { void evaluate() }, [evaluate])
  const handleRetry = useCallback(() => { void refetchRun(); void refetch() }, [refetchRun, refetch])

  const combinedError = runError ?? error

  // The catalogue sidebar is independent of run/hypotheses state, so the two-column shell
  // wraps EVERY state — only the main column swaps between loading/error/results. This keeps
  // the "what the platform can test for" library visible even while the results fetch is
  // loading or has errored (the feature's core goal: never show capability as absent).
  let mainColumn: React.ReactElement
  if (runLoading || loading) {
    mainColumn = <ViewLoadingState />
  } else if (combinedError !== null) {
    mainColumn = <ViewErrorState error={combinedError} onRetry={handleRetry} />
  } else {
    mainColumn = (
      <>
        <ViewHeader runId={runId} evaluating={evaluating} onEvaluate={handleEvaluate} />
        <ContentSwitch
          run={run}
          hypotheses={hypotheses}
          signalCounts={signalCounts}
          totalObservations={totalObservations}
          evaluating={evaluating}
          onEvaluate={handleEvaluate}
          onViewBriefing={onViewBriefing}
        />
      </>
    )
  }

  return (
    <div style={{ padding: CONTENT_PADDING }}>
      <div style={SHELL_STYLE}>
        <div style={MAIN_COLUMN_STYLE}>{mainColumn}</div>
        <CatalogueSidebar caseId={caseId} />
      </div>
    </div>
  )
}
